import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Bell, Calendar, CalendarClock, Check, CheckCircle, ChevronDown, Clock, Copy, Download, Eye,
  FileText, Flame, HelpCircle, Inbox, Mail, MapPin, MessageCircle, MessageSquare, Mic, PauseCircle, Phone,
  PhoneCall, PhoneMissed, PlayCircle, RefreshCw, Search, Settings, ShieldAlert, Sparkles, Tag, Timer, Trash2,
  Upload, UserCheck, UserPlus, Users, Voicemail, Zap,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useModules } from '../../context/FeatureContext';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, CopyButton, DataTable, Dialog, EmptyState, ErrorState, Field, FilterChips, FormRow, KpiTile,
  OverflowMenu, SearchInput, SectionCard, StatRow, StatusPill, TableSkeleton, Tabs, useUrlTab,
  fmtAgo, fmtDate, fmtDateTime, humanise, num, plural,
} from '../../components/ui';
import OutboundCalls from './OutboundCalls';
import WebsiteChatSection, { EmbedCode } from '../Chatbot/WebsiteChatSection';

/* ─── Constants ─────────────────────────────────────────────────────────── */

// The page reads the latest LEAD_LIMIT leads. The Today queue, the tab counts and the callback
// list are worked out from these in the browser.
const LEAD_LIMIT = 200;
const QUEUE_PREVIEW = 25;

const LEAD_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'not_suitable', label: 'Not suitable' },
];

// The channel values the backend stores on receptionist_leads.channel.
const CHANNELS = {
  phone:           { label: 'Phone',     icon: PhoneCall },
  phone_voicemail: { label: 'Voicemail', icon: Voicemail },
  sms:             { label: 'SMS',       icon: MessageSquare },
  web_chat:        { label: 'Web chat',  icon: MessageCircle },
};
const CHANNEL_KEYS = Object.keys(CHANNELS);
const PHONE_CHANNELS = ['phone', 'phone_voicemail', 'sms'];
const channelOf = (c) => CHANNELS[c] || { label: humanise(c) || 'Unknown', icon: MessageCircle };

const CALL_TYPE_LABEL = {
  referral: 'Referral', existing_participant: 'Existing participant', carer_enquiry: 'Carer',
  coordinator_referral: 'Coordinator', general_enquiry: 'General', media: 'Media', other: 'Other',
};

const SCORE_FILTERS = [
  { value: '', label: 'All scores' },
  { value: '61', label: 'Hot (61+)' },
  { value: '31', label: 'Warm and hot (31+)' },
];

const BAND_LABEL = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };

const SELECT_CLS = 'h-8 rounded-lg border border-slate-200 bg-white pl-2.5 text-[13px] text-slate-700 hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';

/* ─── Helpers ───────────────────────────────────────────────────────────── */

const fmtClock = (secs) => {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const fmtDuration = (secs) => (Number(secs) > 0 ? fmtClock(secs) : null);
const toTime = (v) => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};
const clockTime = (v) => {
  const t = toTime(v);
  return t == null ? '—' : new Date(t).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).toLowerCase();
};
// 'just now', '3 h ago' and 'yesterday' read on their own; a plain date needs 'on'.
const ago = (v) => {
  const a = fmtAgo(v);
  return /ago|now|yesterday/.test(a) ? a : `on ${a}`;
};
const scoreOf = (l) => Number(l?.lead_score) || 0;
const scoreBand = (s) => (s >= 61 ? 'hot' : s >= 31 ? 'warm' : 'cold');
const transcriptOf = (lead) => {
  const t = lead?.transcript;
  if (Array.isArray(t)) return t;
  if (typeof t === 'string' && t.trim()) {
    try { const parsed = JSON.parse(t); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
};
const leadName = (l) => l?.name || (l?.channel === 'web_chat' ? 'Website visitor' : 'Unknown caller');
const startOfDay = (now) => { const d = new Date(now); d.setHours(0, 0, 0, 0); return d.getTime(); };
const endOfDay = (now) => { const d = new Date(now); d.setHours(23, 59, 59, 999); return d.getTime(); };

const copyText = (text, done) => {
  const s = String(text || '');
  if (!s) return;
  if (!navigator.clipboard || !navigator.clipboard.writeText) { toast.error('Copying isn’t available in this browser'); return; }
  navigator.clipboard.writeText(s).then(() => toast.success(done)).catch(() => toast.error('Could not copy to the clipboard'));
};

// What the Today view lists. None of this changes a lead; it only decides what needs a person.
const isOpen = (l) => l.status === 'new';                 // nobody has contacted them yet
const isHot = (l) => scoreOf(l) >= 61;                     // the receptionist's own hot threshold
const hasPendingCallback = (l) => !!l.callback_at && !l.callback_done;
// A missed call: a voicemail, or a call logged as busy / no answer (a phone lead with no conversation).
const isMissedCall = (l) => l.channel === 'phone_voicemail'
  || (l.channel === 'phone' && !l.name && !l.ai_summary && transcriptOf(l).length === 0);

function callbackFlags(lead, now) {
  const at = hasPendingCallback(lead) ? toTime(lead.callback_at) : null;
  if (at == null) return { callbackAt: null, overdue: false, dueToday: false, upcoming: false };
  const eod = endOfDay(now);
  return { callbackAt: at, overdue: at < now, dueToday: at >= now && at <= eod, upcoming: at > eod };
}

// Everything that needs someone to act, most urgent first: a safeguarding flag, an overdue
// callback, a hot lead, a callback due later today, then missed calls and voicemails.
function buildQueue(leads, now) {
  const items = [];
  leads.forEach((lead) => {
    const cb = callbackFlags(lead, now);
    const crisis = !!lead.is_crisis && isOpen(lead);
    const hot = isHot(lead) && isOpen(lead);
    const missed = isMissedCall(lead) && isOpen(lead);
    if (!crisis && !cb.overdue && !cb.dueToday && !hot && !missed) return;
    const tier = crisis ? 0 : cb.overdue ? 1 : hot ? 2 : cb.dueToday ? 3 : 4;
    items.push({
      lead, crisis, hot, missed, tier,
      overdue: cb.overdue, dueToday: cb.dueToday, upcoming: false, callbackAt: cb.callbackAt,
    });
  });
  const created = (i) => toTime(i.lead.created_at) || 0;
  return items.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if ((a.tier === 1 || a.tier === 3) && a.callbackAt !== b.callbackAt) return (a.callbackAt || 0) - (b.callbackAt || 0);
    if (a.tier === 2 && scoreOf(a.lead) !== scoreOf(b.lead)) return scoreOf(b.lead) - scoreOf(a.lead);
    return created(a) - created(b);                        // longest waiting first
  });
}

function countToday(leads, now) {
  const sod = startOfDay(now);
  const eod = endOfDay(now);
  const c = { hot: 0, due: 0, overdue: 0, missed: 0, created: 0 };
  leads.forEach((l) => {
    if (isOpen(l) && isHot(l)) c.hot += 1;
    if (isOpen(l) && isMissedCall(l)) c.missed += 1;
    const at = hasPendingCallback(l) ? toTime(l.callback_at) : null;
    if (at != null && at <= eod) { c.due += 1; if (at < now) c.overdue += 1; }
    if ((toTime(l.created_at) || 0) >= sod) c.created += 1;
  });
  return c;
}

/* ─── Small pieces ──────────────────────────────────────────────────────── */

function ScoreBadge({ score }) {
  const s = Number(score) || 0;
  const band = scoreBand(s);
  return <StatusPill status={band} label={`${BAND_LABEL[band]} · ${s}`} />;
}

// A small icon marker in a table cell, with words for screen readers.
function Flag({ icon: Icon, label, className }) {
  return (
    <span title={label} className="inline-flex">
      <Icon size={13} className={clsx('shrink-0', className || 'text-slate-400')} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

function AudioPlayer({ url, label }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  if (!url) return null;
  const name = label.toLowerCase();
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => toast.error('This recording could not be played'));
    else a.pause();
  };
  const seek = (e) => {
    const v = Number(e.target.value);
    const a = audioRef.current;
    if (a && Number.isFinite(v)) a.currentTime = v;
    setTime(v);
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
      <audio ref={audioRef} src={url} preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime || 0)}
        onLoadedMetadata={(e) => setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)} />
      <button type="button" onClick={toggle} aria-label={playing ? `Pause ${name}` : `Play ${name}`}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-primary-600 transition-colors duration-150 hover:bg-primary-50 hover:text-primary-700">
        {playing ? <PauseCircle size={28} aria-hidden="true" /> : <PlayCircle size={28} aria-hidden="true" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="mb-1 text-[12.5px] font-medium text-slate-600">{label}</p>
        <input type="range" min={0} max={duration || 0} step="0.1" value={Math.min(time, duration || 0)} onChange={seek}
          disabled={!duration} aria-label={`Position in ${name}`} className="w-full accent-primary-600 disabled:opacity-50" />
      </div>
      <span className="shrink-0 text-[12px] tabular-nums text-slate-500">
        {fmtClock(time)} / {duration ? fmtClock(duration) : '—'}
      </span>
    </div>
  );
}

/* ─── How the receptionist works (help, collapsed on the daily view) ────── */

const HOW_STEPS = [
  { title: 'A call or chat comes in', tags: '24/7 coverage · Voice and chat · SMS',
    text: 'A caller rings your Twilio number or opens the chat widget on your website. It runs around the clock, so no enquiry is missed.' },
  { title: 'The receptionist answers', tags: 'AI · Australian voice · Natural language',
    text: 'The AI greets callers in an Australian voice (Polly Olivia), works out what they need and qualifies the lead in natural conversation.' },
  { title: 'Qualification and scoring', tags: 'NDIS aware · Automatic scoring · Data extraction',
    text: 'It captures name, phone, NDIS number, funding type, support needs, suburb and urgency, and scores each lead from 0 to 100 (hot, warm or cold).' },
  { title: 'Smart routing', tags: 'Warm transfer · Voicemail · Automatic transcription',
    text: 'Urgent callers are warm-transferred to your office. Others leave a voicemail, which is recorded and transcribed automatically.' },
  { title: 'Team alerts', tags: 'Email alert · SMS alert · Missed-call follow-up',
    text: 'Hot leads send an instant email and SMS alert to your team. Missed callers get an automatic SMS follow-up.' },
  { title: 'Follow-up on this page', tags: 'Call recordings · Transcripts · Callback queue',
    text: 'Every lead, call recording, voicemail and transcript is kept here. Schedule callbacks, update status and track conversions.' },
];

const HOW_FEATURES = [
  { title: 'Call recording', text: 'Every call is recorded and stored automatically.' },
  { title: 'Voicemail transcription', text: 'Missed calls are transcribed to text by AI.' },
  { title: 'SMS handling', text: 'Inbound texts are answered by AI, in 160 characters.' },
  { title: 'Warm transfer', text: 'Urgent callers are put through to your real phone.' },
  { title: 'Missed-call SMS', text: 'Missed callers get an automatic follow-up text.' },
  { title: 'NDIS lead scoring', text: 'A score from 0 to 100, based on 8 qualifying signals.' },
];

const HOW_CHANNELS = [
  { title: 'Live calls', text: 'The receptionist answers, qualifies, and either warm-transfers to your team or schedules a callback.' },
  { title: 'Voicemails', text: 'Recorded and transcribed by AI. The caller gets an SMS confirming you’ll be in touch.' },
  { title: 'Inbound SMS', text: 'Text enquiries are answered by AI within seconds, and the full conversation is saved as a lead.' },
  { title: 'Web chat', text: 'Visitors chat with the widget on your website. Each conversation is saved as a web-chat lead.' },
];

const HOW_CALLBACKS = [
  { title: 'The receptionist schedules them', text: 'When a caller asks to be called back, it records their preferred time and creates the callback.' },
  { title: 'You’re alerted', text: 'Hot leads send an immediate email and SMS to your team. The Callbacks tab lists every callback, with overdue ones flagged.' },
  { title: 'Call from your device', text: 'Use Call back next to any callback to dial from your phone or computer, then mark it done.' },
];

const SCORE_SIGNALS = [
  ['Name captured', 10], ['Contact (phone or email)', 15], ['NDIS number given', 20], ['Funding type known', 15],
  ['Support needs stated', 10], ['Suburb mentioned', 5], ['Urgent need', 25], ['Starting this week', 10],
];

const SCORE_BANDS = [
  { band: 'hot', range: '61+', text: 'Instant alert and warm transfer' },
  { band: 'warm', range: '31–60', text: 'Email alert to the team' },
  { band: 'cold', range: '0–30', text: 'Saved for follow-up' },
];

function HelpGroup({ title, children }) {
  return (
    <div>
      <h3 className="mb-3 text-[13px] font-semibold text-slate-800">{title}</h3>
      {children}
    </div>
  );
}

function DefList({ items, cols = 3 }) {
  return (
    <dl className={clsx('grid gap-x-6 gap-y-3', cols === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3')}>
      {items.map((i) => (
        <div key={i.title}>
          <dt className="text-[13px] font-medium text-slate-800">{i.title}</dt>
          <dd className="mt-0.5 text-[13px] leading-relaxed text-slate-600">{i.text}</dd>
        </div>
      ))}
    </dl>
  );
}

function HowItWorks({ open, onToggle, setupNote }) {
  const bodyId = useId();
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" aria-label="How the receptionist works">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={bodyId}
        className="flex w-full items-center gap-2.5 px-5 py-3.5 text-left transition-colors duration-150 hover:bg-slate-50">
        <HelpCircle size={16} className="shrink-0 text-slate-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-800">How the receptionist works</span>
        <span className="hidden text-[13px] text-slate-500 md:inline">Call handling, channels, callbacks and lead scoring</span>
        <ChevronDown size={16} aria-hidden="true"
          className={clsx('ml-auto shrink-0 text-slate-400 transition-transform duration-150', open && 'rotate-180')} />
      </button>
      <div id={bodyId} hidden={!open} className="space-y-7 border-t border-slate-100 px-5 py-5">
        {setupNote}
        <p className="max-w-3xl text-[13px] leading-relaxed text-slate-600">
          The receptionist uses AI and Twilio to answer calls, qualify NDIS leads, record voicemails,
          warm-transfer urgent enquiries and alert your team, around the clock.
        </p>
        <HelpGroup title="How a call is handled">
          <ol className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {HOW_STEPS.map((s, i) => (
              <li key={s.title} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[12px] font-semibold tabular-nums text-slate-600">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-800">{s.title}</p>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">{s.text}</p>
                  <p className="mt-1 text-[12px] text-slate-500">{s.tags}</p>
                </div>
              </li>
            ))}
          </ol>
        </HelpGroup>
        <HelpGroup title="What it can do"><DefList items={HOW_FEATURES} /></HelpGroup>
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-2">
          <HelpGroup title="Channels"><DefList items={HOW_CHANNELS} cols={1} /></HelpGroup>
          <HelpGroup title="Callbacks"><DefList items={HOW_CALLBACKS} cols={1} /></HelpGroup>
        </div>
        <HelpGroup title="Lead scoring (0–100)">
          <div className="grid max-w-3xl grid-cols-1 gap-x-8 sm:grid-cols-2">
            {SCORE_SIGNALS.map(([signal, points]) => (
              <div key={signal} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-[13px]">
                <span className="text-slate-600">{signal}</span>
                <span className="font-semibold tabular-nums text-slate-800">+{points}</span>
              </div>
            ))}
          </div>
          <ul className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6">
            {SCORE_BANDS.map((b) => (
              <li key={b.band} className="flex items-center gap-2 text-[13px] text-slate-600">
                <StatusPill status={b.band} label={`${BAND_LABEL[b.band]} · ${b.range}`} />
                {b.text}
              </li>
            ))}
          </ul>
        </HelpGroup>
      </div>
    </section>
  );
}

/* ─── Today: one row per lead that needs someone ─────────────────────────── */

function QueueRow({ item, busy, onOpen, onMarkContacted, onCallbackDone }) {
  const { lead } = item;
  const name = leadName(lead);
  const ch = channelOf(lead.channel);
  const ChIcon = ch.icon;
  const hasCallback = item.overdue || item.dueToday || item.upcoming;
  const reasons = [
    item.crisis && { key: 'crisis', label: 'Safeguarding flag', tone: 'danger' },
    item.overdue && { key: 'overdue', label: 'Callback overdue', tone: 'danger' },
    item.hot && { key: 'hot', label: `Hot · ${scoreOf(lead)}`, tone: 'danger' },
    item.dueToday && { key: 'today', label: `Callback at ${clockTime(lead.callback_at)}`, tone: 'warning' },
    item.upcoming && { key: 'later', label: 'Callback booked', tone: 'info' },
    item.missed && { key: 'missed', label: lead.channel === 'phone_voicemail' ? 'Voicemail' : 'Missed call', tone: 'warning' },
  ].filter(Boolean);
  const when = item.overdue ? `Callback was due ${ago(lead.callback_at)}`
    : item.dueToday ? `Callback due at ${clockTime(lead.callback_at)} today`
      : item.upcoming ? `Callback due ${fmtDateTime(lead.callback_at)}`
        : `Came in ${ago(lead.created_at)}`;
  const detail = lead.support_needs || lead.voicemail_text || lead.ai_summary || '';

  return (
    <li className="flex flex-col gap-3 px-4 py-3.5 sm:px-5 lg:flex-row lg:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <button type="button" onClick={() => onOpen(lead)}
            className={clsx('text-left text-sm font-semibold underline-offset-2 hover:text-primary-700 hover:underline',
              lead.name ? 'text-slate-900' : 'text-slate-600')}>
            {name}
          </button>
          {reasons.map((r) => <StatusPill key={r.key} status={r.key} tone={r.tone} label={r.label} />)}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <ChIcon size={13} className="text-slate-400" aria-hidden="true" />{ch.label}
          </span>
          {lead.phone && <span className="tabular-nums">{lead.phone}</span>}
          <span>{when}</span>
        </div>
        {detail && <p className="mt-1 max-w-3xl truncate text-[13px] text-slate-600" title={detail}>{detail}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">
        {lead.phone ? (
          <a href={`tel:${lead.phone}`} className="btn-secondary btn-sm h-9">
            <Phone size={14} aria-hidden="true" />Call back<span className="sr-only"> {name}</span>
          </a>
        ) : lead.email ? (
          <a href={`mailto:${lead.email}`} className="btn-secondary btn-sm h-9">
            <Mail size={14} aria-hidden="true" />Email<span className="sr-only"> {name}</span>
          </a>
        ) : null}
        {hasCallback ? (
          <button type="button" disabled={busy} onClick={() => onCallbackDone(lead)} className="btn-secondary btn-sm h-9">
            <Check size={14} aria-hidden="true" />Callback done<span className="sr-only"> for {name}</span>
          </button>
        ) : isOpen(lead) ? (
          <button type="button" disabled={busy} onClick={() => onMarkContacted(lead)} className="btn-secondary btn-sm h-9">
            <UserCheck size={14} aria-hidden="true" />Mark contacted<span className="sr-only"> {name}</span>
          </button>
        ) : null}
        <OverflowMenu label={`More actions for ${name}`} size={16} className="p-2.5" items={[
          { label: 'Open lead', icon: Eye, onClick: () => onOpen(lead) },
          { label: hasCallback ? 'Reschedule callback' : 'Schedule callback', icon: CalendarClock, onClick: () => onOpen(lead, 'callback') },
          hasCallback && isOpen(lead) && { label: 'Mark contacted', icon: UserCheck, onClick: () => onMarkContacted(lead) },
          lead.phone && { label: 'Copy phone number', icon: Copy, onClick: () => copyText(lead.phone, 'Phone number copied') },
        ]} />
      </div>
    </li>
  );
}

function AppointmentsCard({ appointments, total, onOpen }) {
  const count = Math.max(Number(total) || 0, appointments.length);
  if (!count) return null;
  const shown = appointments.slice(0, 5);
  return (
    <SectionCard title="Upcoming intake appointments" icon={Calendar} bodyClass=""
      actions={<span className="text-[12.5px] tabular-nums text-slate-500">{num(count)}</span>}>
      {shown.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {shown.map((l) => (
            <li key={l.id} className="flex flex-col gap-1 px-5 py-2.5 text-[13px] sm:flex-row sm:items-center sm:gap-3">
              <button type="button" onClick={() => onOpen(l)}
                className="text-left font-medium text-slate-800 underline-offset-2 hover:text-primary-700 hover:underline">
                {leadName(l)}
              </button>
              {l.support_needs && <span className="min-w-0 truncate text-slate-500">{l.support_needs}</span>}
              <span className="whitespace-nowrap tabular-nums text-slate-700 sm:ml-auto">{fmtDateTime(l.appointment_at)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className={clsx('px-5 py-2.5 text-[12.5px] text-slate-500', shown.length > 0 && 'border-t border-slate-100')}>
        Booked by the receptionist during a call or chat. Open a lead to see the details.
        {count > shown.length ? ` Showing the next ${num(shown.length)} of ${num(count)}.` : ''}
      </p>
    </SectionCard>
  );
}

/* ─── All leads: breakdowns under the list ──────────────────────────────── */

function Bar({ pct }) {
  return (
    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
      <div className="h-full rounded-full bg-slate-400" style={{ width: `${pct}%` }} />
    </div>
  );
}

function PeakHours({ hours }) {
  const count = (h) => Number(hours.find((p) => Number(p.hour) === h)?.count) || 0;
  const max = Math.max(1, ...hours.map((p) => Number(p.count) || 0));
  const totalCalls = hours.reduce((a, p) => a + (Number(p.count) || 0), 0);
  const label = (h) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);
  let busiest = null;
  for (let h = 0; h < 24; h += 1) if (!busiest || count(h) > busiest.n) busiest = { h, n: count(h) };
  return (
    <>
      <div className="flex h-20 items-end gap-0.5" role="img"
        aria-label={`Calls by hour of the day. Busiest hour: ${busiest && busiest.n ? `${label(busiest.h)}, ${plural(busiest.n, 'call')}` : 'none yet'}.`}>
        {Array.from({ length: 24 }, (_, h) => {
          const n = count(h);
          const pct = Math.round((n / max) * 100);
          return (
            <div key={h} className="flex h-full flex-1 flex-col justify-end" title={`${label(h)}: ${plural(n, 'call')}`}>
              <div className={clsx('w-full rounded-sm', n > 0 ? 'bg-slate-500' : 'bg-slate-100')}
                style={{ height: `${Math.max(pct, n > 0 ? 8 : 4)}%` }} />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex gap-0.5" aria-hidden="true">
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="flex-1 whitespace-nowrap text-center text-[10px] text-slate-500">{h % 3 === 0 ? label(h) : ''}</span>
        ))}
      </div>
      <p className="mt-2 text-[12.5px] text-slate-500">Based on {plural(totalCalls, 'logged call')}.</p>
    </>
  );
}

function Breakdown({ stats }) {
  const total = Number(stats.total) || 0;
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const channels = Object.entries(stats.byChannel || {}).map(([k, v]) => [k, Number(v) || 0]).sort((a, b) => b[1] - a[1]);
  const known = LEAD_STATUSES.map((s) => s.value);
  const statuses = [...known, ...Object.keys(stats.byStatus || {}).filter((k) => !known.includes(k))]
    .map((k) => [k, Number(stats.byStatus?.[k]) || 0]);
  const hours = Array.isArray(stats.peak_hours) ? stats.peak_hours : [];
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <SectionCard title="Leads by channel" bodyClass="space-y-3 px-5 py-4">
        {channels.length === 0 ? <p className="text-[13px] text-slate-500">No leads yet.</p> : channels.map(([key, n]) => {
          const ch = channelOf(key);
          const Icon = ch.icon;
          return (
            <div key={key}>
              <div className="flex items-center gap-2 text-[13px]">
                <Icon size={14} className="text-slate-400" aria-hidden="true" />
                <span className="text-slate-700">{ch.label}</span>
                <span className="ml-auto font-semibold tabular-nums text-slate-900">{num(n)}</span>
                <span className="w-10 text-right tabular-nums text-slate-500">{pct(n)}%</span>
              </div>
              <Bar pct={pct(n)} />
            </div>
          );
        })}
      </SectionCard>
      <SectionCard title="Leads by status" bodyClass="space-y-2.5 px-5 py-4">
        {statuses.map(([key, n]) => (
          <div key={key} className="flex items-center gap-2 text-[13px]">
            <StatusPill status={key} />
            <span className="ml-auto font-semibold tabular-nums text-slate-900">{num(n)}</span>
            <span className="w-10 text-right tabular-nums text-slate-500">{pct(n)}%</span>
          </div>
        ))}
      </SectionCard>
      <SectionCard title="Peak call hours" bodyClass="px-5 py-4">
        {hours.length === 0 ? <p className="text-[13px] text-slate-500">No calls logged yet.</p> : <PeakHours hours={hours} />}
      </SectionCard>
    </div>
  );
}

/* ─── The lead list (All leads, Phone and voicemail, Callbacks) ─────────── */

function LeadList({
  title, leads, loading, error, onRetry, search, onSearch, channel, onChannel, status, onStatus, score, onScore,
  channelCounts, total, assignees = [], onOpen, onDelete, onAssign, onMarkContacted, onClearFilters,
  showCallback = false, empty = {}, notice = null, limitReached = false, now,
}) {
  const assigneeName = (id) => (id ? assignees.find((a) => a.id === id)?.name || null : null);
  const q = search.trim().toLowerCase();
  const rows = useMemo(() => leads.filter((l) => (!status || l.status === status)
    && (!channel || l.channel === channel)
    && (!score || scoreOf(l) >= Number(score))
    && (!q || [l.name, l.phone, l.email, l.suburb, l.support_needs, l.ndis_number]
      .some((v) => String(v || '').toLowerCase().includes(q)))), [leads, status, channel, score, q]);
  const filtersOn = !!(q || status || channel || score);

  const chips = [
    { value: null, label: 'All channels', count: total },
    ...['phone', 'phone_voicemail', 'sms', 'web_chat'].map((c) => ({
      value: c, label: CHANNELS[c].label, count: channelCounts ? Number(channelCounts[c]) || 0 : undefined,
    })),
  ];

  const columns = [
    {
      key: 'name', header: 'Lead', sortable: true, sortValue: (l) => (l.name ? l.name.toLowerCase() : null),
      render: (l) => {
        const who = assigneeName(l.assigned_to);
        return (
          <div className="min-w-[11rem] leading-tight">
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(l); }}
                className={clsx('text-left font-semibold underline-offset-2 hover:text-primary-700 hover:underline',
                  l.name ? 'text-slate-900' : 'text-slate-600')}>
                {leadName(l)}
              </button>
              {l.is_crisis && <Flag icon={ShieldAlert} label="Safeguarding flag" className="text-rose-500" />}
              {(l.recording_url || l.voicemail_url) && <Flag icon={Mic} label="Has a recording" />}
              {hasPendingCallback(l) && <Flag icon={Bell} label="Callback pending" className="text-amber-500" />}
            </div>
            {(l.suburb || who) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12px] text-slate-500">
                {l.suburb && (
                  <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-slate-400" aria-hidden="true" />{l.suburb}</span>
                )}
                {who && (
                  <span className="inline-flex items-center gap-1">
                    <UserCheck size={11} className="text-slate-400" aria-hidden="true" /><span className="sr-only">Assigned to </span>{who}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'contact', header: 'Contact',
      render: (l) => (
        <div className="leading-tight">
          {l.phone && (
            <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()}
              className="block whitespace-nowrap tabular-nums text-slate-700 underline-offset-2 hover:text-primary-700 hover:underline">
              {l.phone}
            </a>
          )}
          {l.email && <span className="mt-0.5 block max-w-[14rem] truncate text-[12px] text-slate-500" title={l.email}>{l.email}</span>}
          {!l.phone && !l.email && <span className="text-slate-500">—</span>}
        </div>
      ),
    },
    {
      key: 'need', header: 'Need / type',
      render: (l) => (
        <div className="leading-tight">
          <span className="block max-w-[12rem] truncate text-slate-700" title={l.support_needs || undefined}>{l.support_needs || '—'}</span>
          {l.call_type && <span className="mt-0.5 block text-[12px] text-slate-500">{CALL_TYPE_LABEL[l.call_type] || humanise(l.call_type)}</span>}
        </div>
      ),
    },
    {
      key: 'funding_type', header: 'Funding', sortable: true, sortValue: (l) => l.funding_type || null,
      render: (l) => <span className="text-slate-700">{l.funding_type ? humanise(l.funding_type) : '—'}</span>,
    },
    {
      key: 'lead_score', header: 'Score', sortable: true, defaultDir: 'desc', sortValue: (l) => scoreOf(l),
      render: (l) => <ScoreBadge score={l.lead_score} />,
    },
    {
      key: 'status', header: 'Status', sortable: true, sortValue: (l) => l.status || null,
      render: (l) => <StatusPill status={l.status || 'new'} />,
    },
    {
      key: 'channel', header: 'Channel', sortable: true, sortValue: (l) => channelOf(l.channel).label,
      render: (l) => {
        const ch = channelOf(l.channel);
        const Icon = ch.icon;
        return (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-700">
            <Icon size={13} className="text-slate-400" aria-hidden="true" />{ch.label}
          </span>
        );
      },
    },
    {
      key: 'call_duration', header: 'Duration', align: 'right', sortable: true, sortValue: (l) => Number(l.call_duration) || null,
      render: (l) => <span className="tabular-nums text-slate-700">{fmtDuration(l.call_duration) || '—'}</span>,
    },
    {
      key: 'created_at', header: 'Date', sortable: true, defaultDir: 'desc', sortValue: (l) => toTime(l.created_at),
      render: (l) => (
        <span className="whitespace-nowrap tabular-nums text-slate-700" title={fmtDateTime(l.created_at)}>{fmtDate(l.created_at)}</span>
      ),
    },
    showCallback && {
      key: 'callback_at', header: 'Callback', sortable: true, sortValue: (l) => toTime(l.callback_at),
      render: (l) => {
        if (!l.callback_at) return <span className="text-slate-500">—</span>;
        const at = toTime(l.callback_at);
        return (
          <div className="leading-tight">
            <span className="block whitespace-nowrap tabular-nums text-slate-700">{fmtDateTime(l.callback_at)}</span>
            {l.callback_done ? <span className="mt-0.5 block text-[12px] font-medium text-emerald-700">Done</span>
              : at != null && at < now ? <span className="mt-0.5 block text-[12px] font-medium text-rose-700">Overdue</span> : null}
          </div>
        );
      },
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', width: '3.5rem',
      render: (l) => (
        <OverflowMenu label={`Actions for ${leadName(l)}`} size={16} className="p-2.5" items={[
          { label: 'Open lead', icon: Eye, onClick: () => onOpen(l) },
          l.phone && { label: 'Call', icon: Phone, onClick: () => { window.location.href = `tel:${l.phone}`; } },
          l.phone && { label: 'Copy phone number', icon: Copy, onClick: () => copyText(l.phone, 'Phone number copied') },
          { label: hasPendingCallback(l) ? 'Reschedule callback' : 'Schedule callback', icon: CalendarClock, onClick: () => onOpen(l, 'callback') },
          onMarkContacted && isOpen(l) && { label: 'Mark contacted', icon: UserCheck, onClick: () => onMarkContacted(l) },
          onAssign && l.assigned_to && { divider: true },
          onAssign && l.assigned_to && { label: 'Unassign', icon: UserPlus, onClick: () => onAssign(l.id, '') },
          onDelete && { divider: true },
          onDelete && { label: 'Delete lead', icon: Trash2, danger: true, onClick: () => onDelete(l) },
        ]} />
      ),
    },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
          {title && <h2 className="text-[13px] font-semibold text-slate-800 lg:mr-1">{title}</h2>}
          <SearchInput value={search} onChange={onSearch} placeholder="Search name, phone, email, suburb or NDIS number" className="w-full lg:w-80" />
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Filter by status" value={status} onChange={(e) => onStatus(e.target.value)} className={SELECT_CLS}>
              <option value="">All statuses</option>
              {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select aria-label="Filter by score" value={score} onChange={(e) => onScore(e.target.value)} className={SELECT_CLS}>
              {SCORE_FILTERS.map((s) => <option key={s.value || 'all'} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <p className="text-[13px] tabular-nums text-slate-500 lg:ml-auto" aria-live="polite">
            {loading ? 'Loading…' : q ? `${num(rows.length)} of ${plural(leads.length, 'lead')}` : plural(rows.length, 'lead')}
          </p>
        </div>
        <FilterChips options={chips} value={channel || null} onChange={(v) => onChannel(v || '')} multi={false} ariaLabel="Filter by channel" />
        {notice}
      </div>
      <DataTable
        label={title || 'Leads'}
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        error={error}
        onRetry={onRetry}
        stickyHeader={false}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        onRowClick={(l) => onOpen(l)}
        empty={filtersOn ? {
          icon: Search,
          title: 'No leads match these filters',
          hint: 'Try another channel or status, or clear the search.',
          action: <button type="button" className="btn-secondary btn-sm h-9" onClick={onClearFilters}>Clear filters</button>,
        } : empty}
      />
      {limitReached && !loading && !error && (
        <p className="border-t border-slate-100 px-4 py-2.5 text-[12.5px] text-slate-500">
          Showing the latest {num(LEAD_LIMIT)} leads. Export CSV includes them all.
        </p>
      )}
    </div>
  );
}

/* ─── Lead detail ────────────────────────────────────────────────────────── */

function LeadModal({ lead, initialTab = 'details', onClose, onUpdated, assignees = [], onAssign, onDelete, onCallbackDone }) {
  const [tab, setTab] = useState(initialTab);
  const [status, setStatus] = useState(lead.status || 'new');
  const [notes, setNotes] = useState(lead.notes || '');
  const [cbDate, setCbDate] = useState('');
  const [cbTime, setCbTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Escape reaches both this dialog and the delete confirmation; this one stays open underneath.
  const busyRef = useRef(false);
  const requestClose = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);

  const transcript = transcriptOf(lead);
  const ch = channelOf(lead.channel);
  const duration = fmtDuration(lead.call_duration);
  const assignedName = assignees.find((a) => a.id === lead.assigned_to)?.name || null;
  const cbAt = toTime(lead.callback_at);
  const cbOverdue = hasPendingCallback(lead) && cbAt != null && cbAt < Date.now();
  const linkCls = 'text-primary-700 underline-offset-2 hover:underline';

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/receptionist/leads/${lead.id}`, { status, notes });
      toast.success('Lead updated');
      onUpdated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not update the lead');
    } finally { setSaving(false); }
  };

  const scheduleCallback = async () => {
    if (!cbDate) { toast.error('Choose a date for the callback'); return; }
    const at = new Date(`${cbDate}T${cbTime || '09:00'}`).toISOString();
    setSaving(true);
    try {
      await api.post('/receptionist/callback', { leadId: lead.id, phoneNumber: lead.phone, callbackAt: at });
      toast.success('Callback scheduled');
      setCbDate('');
      setCbTime('');
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not schedule the callback');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    busyRef.current = true;
    setDeleting(true);
    try { await onDelete(lead); } finally { busyRef.current = false; setDeleting(false); }
  };

  const details = [
    [Phone, 'Phone', lead.phone ? <a href={`tel:${lead.phone}`} className={linkCls}>{lead.phone}</a> : null],
    [Mail, 'Email', lead.email ? <a href={`mailto:${lead.email}`} className={clsx(linkCls, 'break-all')}>{lead.email}</a> : null],
    [Tag, 'NDIS number', lead.ndis_number],
    [FileText, 'Funding', lead.funding_type ? humanise(lead.funding_type) : null],
    [MapPin, 'Suburb', lead.suburb],
    [Clock, 'Urgency', lead.urgency ? humanise(lead.urgency) : null],
    [PhoneCall, 'Call type', lead.call_type ? (CALL_TYPE_LABEL[lead.call_type] || humanise(lead.call_type)) : null],
    [Timer, 'Duration', duration],
    [Calendar, 'Appointment', lead.appointment_at ? fmtDateTime(lead.appointment_at) : null],
  ].filter(([, , value]) => value);

  return (
    <Dialog open onClose={requestClose} size="lg" title={leadName(lead)}
      description={[ch.label, fmtDateTime(lead.created_at), duration].filter(Boolean).join(' · ')}
      footer={(
        <>
          {onDelete && (
            <button type="button" onClick={remove} disabled={deleting}
              className="btn-ghost mr-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700">
              <Trash2 size={15} aria-hidden="true" /> Delete lead
            </button>
          )}
          <button type="button" onClick={requestClose} className="btn-secondary">Close</button>
          {tab === 'details' && (
            <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save changes'}</button>
          )}
        </>
      )}>
      <div data-autofocus tabIndex={-1} className="mb-3 flex flex-wrap items-center gap-2 outline-none">
        <ScoreBadge score={lead.lead_score} />
        <StatusPill status={lead.status || 'new'} />
        {lead.is_crisis && <StatusPill status="crisis" tone="danger" label="Safeguarding flag" />}
      </div>
      <Tabs value={tab} onChange={setTab} ariaLabel="Lead sections" tabs={[
        { id: 'details', label: 'Details' },
        { id: 'recordings', label: 'Recordings' },
        { id: 'transcript', label: 'Transcript', count: transcript.length },
        { id: 'callback', label: 'Callback' },
      ]} />

      <div className="pt-4">
        {tab === 'details' && (
          <div className="space-y-5">
            {details.length > 0 && (
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {details.map(([Icon, label, value]) => (
                  <div key={label} className="flex min-w-0 items-start gap-2 text-[13px]">
                    <Icon size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
                    <dt className="shrink-0 text-slate-500">{label}</dt>
                    <dd className="min-w-0 font-medium text-slate-800">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {lead.support_needs && (
              <div>
                <p className="text-[12.5px] font-semibold text-slate-700">Support needs</p>
                <p className="mt-1 text-[13px] leading-relaxed text-slate-700">{lead.support_needs}</p>
              </div>
            )}
            {lead.is_crisis && (
              <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4" role="note">
                <ShieldAlert size={18} className="mt-0.5 shrink-0 text-rose-600" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-rose-800">Crisis or safeguarding flag</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-rose-700">
                    This caller was flagged as a possible crisis or safeguarding concern. Follow up straight away,
                    and contact the NDIS Commission on 1800 035 544 if required.
                  </p>
                </div>
              </div>
            )}
            {lead.ai_summary && (
              <div>
                <p className="text-[12.5px] font-semibold text-slate-700">AI summary</p>
                <p className="mt-1 rounded-lg bg-slate-50 p-3 text-[13px] leading-relaxed text-slate-700">{lead.ai_summary}</p>
              </div>
            )}
            <fieldset>
              <legend className="mb-2 text-[13px] font-medium text-slate-700">Status</legend>
              <div className="flex flex-wrap gap-2">
                {LEAD_STATUSES.map((s) => (
                  <button key={s.value} type="button" aria-pressed={status === s.value} onClick={() => setStatus(s.value)}
                    className={clsx('h-9 rounded-lg border px-3 text-[13px] font-medium transition-colors duration-150',
                      status === s.value ? 'border-primary-300 bg-primary-50 text-primary-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50')}>
                    {s.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <Field label="Notes" hint="Status and notes are saved with Save changes.">
              {(id) => (
                <textarea id={id} rows={3} className="input-field resize-y" value={notes}
                  onChange={(e) => setNotes(e.target.value)} placeholder="Add notes" />
              )}
            </Field>
            <Field label="Assigned to"
              hint={assignedName ? `Currently assigned to ${assignedName}. A change here saves straight away.` : 'A change here saves straight away.'}>
              {(id) => (
                <select id={id} className="input-field" value={lead.assigned_to || ''}
                  onChange={(e) => { if (onAssign) onAssign(lead.id, e.target.value); }}>
                  <option value="">Unassigned</option>
                  {assignees.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}{a.role ? ` · ${humanise(a.role)}` : ''}</option>
                  ))}
                </select>
              )}
            </Field>
          </div>
        )}

        {tab === 'recordings' && (
          <div className="space-y-5">
            {lead.recording_url ? (
              <div>
                <h3 className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-slate-700">
                  <PhoneCall size={14} className="text-slate-400" aria-hidden="true" />Call recording
                  {duration && <span className="font-normal text-slate-500">· {duration}</span>}
                </h3>
                <AudioPlayer url={lead.recording_url} label="Call recording" />
              </div>
            ) : lead.channel === 'web_chat' && !lead.voicemail_url ? (
              <EmptyState icon={MessageCircle} title="Web chat lead, no audio"
                hint="Website conversations are text only. The Transcript tab has the full chat." />
            ) : (
              <EmptyState icon={PhoneMissed} title="No call recording" hint="Recordings are saved automatically once Twilio is set up." />
            )}
            {lead.voicemail_url && (
              <div>
                <h3 className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-slate-700">
                  <Voicemail size={14} className="text-slate-400" aria-hidden="true" />Voicemail
                </h3>
                <AudioPlayer url={lead.voicemail_url} label="Voicemail" />
                {lead.voicemail_text && (
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700">
                      <Mic size={13} className="text-slate-400" aria-hidden="true" />Voicemail transcription
                    </p>
                    <p className="text-[13px] italic leading-relaxed text-slate-700">“{lead.voicemail_text}”</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'transcript' && (
          transcript.length === 0 ? (
            <EmptyState icon={MessageCircle} title="No transcript" hint="This conversation didn't save a transcript." />
          ) : (
            <ol className="space-y-2.5">
              {transcript.map((m, i) => {
                const caller = m?.role === 'user';
                return (
                  <li key={i} className={clsx('flex flex-col', caller ? 'items-end' : 'items-start')}>
                    <span className="mb-0.5 text-[11.5px] font-medium text-slate-500">
                      {caller ? (lead.channel === 'web_chat' ? 'Visitor' : 'Caller') : 'Receptionist'}
                    </span>
                    <span className={clsx('max-w-[82%] whitespace-pre-wrap rounded-xl px-3 py-2 text-[13px] leading-relaxed',
                      caller ? 'bg-slate-800 text-white' : 'border border-slate-200 bg-white text-slate-700')}>
                      {m?.content || ''}
                    </span>
                  </li>
                );
              })}
            </ol>
          )
        )}

        {tab === 'callback' && (
          <div className="space-y-5">
            {lead.callback_at && (
              <div className={clsx('rounded-xl border p-4',
                lead.callback_done ? 'border-emerald-200 bg-emerald-50' : cbOverdue ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50')}>
                <p className={clsx('text-[12.5px] font-semibold',
                  lead.callback_done ? 'text-emerald-800' : cbOverdue ? 'text-rose-800' : 'text-amber-800')}>
                  {lead.callback_done ? 'Callback completed' : cbOverdue ? 'Callback overdue' : 'Callback scheduled'}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900">{fmtDateTime(lead.callback_at)}</p>
                {!lead.callback_done && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className="btn-secondary btn-sm h-9">
                        <Phone size={14} aria-hidden="true" />Call {lead.phone} now
                      </a>
                    )}
                    {onCallbackDone && (
                      <button type="button" onClick={() => onCallbackDone(lead)} className="btn-secondary btn-sm h-9">
                        <Check size={14} aria-hidden="true" />Mark callback done
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <div>
              <h3 className="mb-3 text-[13px] font-semibold text-slate-700">
                {hasPendingCallback(lead) ? 'Reschedule the callback' : 'Schedule a callback'}
              </h3>
              <FormRow cols={2}>
                <Field label="Date" required>
                  {(id) => <input id={id} type="date" className="input-field" value={cbDate} onChange={(e) => setCbDate(e.target.value)} />}
                </Field>
                <Field label="Time" hint="9:00 am if left blank">
                  {(id) => <input id={id} type="time" className="input-field" value={cbTime} onChange={(e) => setCbTime(e.target.value)} />}
                </Field>
              </FormRow>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[12.5px] text-slate-500">{lead.phone ? `Will call ${lead.phone}` : ''}</p>
                <button type="button" onClick={scheduleCallback} disabled={saving || !cbDate} className="btn-primary btn-sm h-9">
                  <CalendarClock size={14} aria-hidden="true" />Schedule callback
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

/* ─── Super admin: Twilio / company profile / website widget ───────────── */

const PROFILE_KEYS = [
  'company_name', 'company_phone', 'company_email', 'company_website',
  'abn', 'ndis_provider_number', 'ndis_registration_groups', 'registration_expiry',
  'service_areas', 'operating_hours', 'languages_spoken', 'disability_types',
  'services', 'pricing_info', 'intake_process', 'referral_process',
  'staffing_info', 'key_contacts', 'welcome_message', 'additional_info',
];
const PROFILE_DEFAULTS = {
  ...PROFILE_KEYS.reduce((o, k) => ({ ...o, [k]: '' }), {}),
  primary_color: '#4f46e5',
  widget_position: 'right',
  quick_replies: 'I need NDIS support\nI have a referral\nWhat services do you offer?\nI need urgent help',
};

const INSTALL_STEPS = [
  { platform: 'WordPress', steps: 'Go to Appearance → Theme Editor → footer.php (or use a header and footer plugin). Paste the code before </body>.' },
  { platform: 'Wix', steps: 'Settings → Custom Code → Add Code at the bottom of the page → Body. Paste and save.' },
  { platform: 'Squarespace', steps: 'Settings → Advanced → Code Injection → Footer. Paste and save.' },
  { platform: 'Any HTML site', steps: 'Open your HTML file and paste the code just before the closing </body> tag on every page that should show the chat.' },
];

const WIDGET_OUTCOMES = [
  'The receptionist greets them using your company profile (name, services and areas).',
  'The lead is qualified and scored: name, phone, NDIS number, funding type and suburb.',
  'Hot leads (score 61+) send an instant email alert to your team.',
  'Appointment requests are logged with the date and time.',
  'Every conversation appears on this page under All leads, in the Web chat channel.',
];

function ProfileSection({ title, hint, children }) {
  return (
    <section className="space-y-3 border-t border-slate-100 pt-5">
      <div>
        <h3 className="text-[13px] font-semibold text-slate-800">{title}</h3>
        {hint && <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function TwilioSettingsModal({ onClose, onSaved }) {
  const [tab, setTab] = useState('twilio');
  const [twilio, setTwilio] = useState({ account_sid: '', auth_token: '', phone_number: '', transfer_number: '' });
  const [tokenSet, setTokenSet] = useState(false);
  const [tokenLast4, setTokenLast4] = useState('');
  const [profile, setProfile] = useState(PROFILE_DEFAULTS);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const backendBase = window.location.origin.replace(':3000', ':5000');
  const widgetUrl = `${backendBase}/api/receptionist/widget.js`;
  const embedCode = `<!-- AI Receptionist Widget — add before </body> -->\n<script src="${widgetUrl}" async></script>`;
  const voiceUrl = `${backendBase}/api/receptionist/voice/incoming`;
  const smsUrl = `${backendBase}/api/receptionist/sms/incoming`;

  useEffect(() => {
    Promise.all([
      api.get('/receptionist/twilio-config').catch(() => ({ data: {} })),
      api.get('/receptionist/config').catch(() => ({ data: {} })),
    ]).then(([tc, pc]) => {
      const t = tc.data || {};
      setTwilio({ account_sid: t.account_sid || '', auth_token: t.auth_token || '', phone_number: t.phone_number || '', transfer_number: t.transfer_number || '' });
      setTokenSet(!!t.auth_token_set);
      setTokenLast4(t.auth_token_last4 || '');
      const p = pc.data || {};
      const next = {};
      PROFILE_KEYS.forEach((k) => { next[k] = p[k] || ''; });
      next.primary_color = p.primary_color || '#4f46e5';
      next.widget_position = p.widget_position || 'right';
      next.quick_replies = Array.isArray(p.quick_replies) ? p.quick_replies.join('\n') : (p.quick_replies || '');
      setProfile(next);
    }).finally(() => setLoading(false));
  }, []);

  const setT = (k) => (e) => { const v = e.target.value; setTwilio((f) => ({ ...f, [k]: v })); };
  const setP = (k) => (e) => { const v = e.target.value; setProfile((f) => ({ ...f, [k]: v })); };

  const saveTwilio = async () => {
    // A saved token is never sent back to the browser; leaving the field blank keeps it.
    if (!twilio.account_sid || !twilio.phone_number || (!twilio.auth_token && !tokenSet)) {
      toast.error('Account SID, auth token and phone number are required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/receptionist/twilio-config', twilio);
      toast.success(data.configured ? 'Twilio is active. Voice calls are on.' : 'Twilio settings saved');
      if (twilio.auth_token) {
        setTokenSet(true);
        setTokenLast4(twilio.auth_token.slice(-4));
        setTwilio((f) => ({ ...f, auth_token: '' }));
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save the Twilio settings');
    } finally { setSaving(false); }
  };

  const analyzeDoc = async () => {
    if (!uploadFile && !uploadDesc.trim()) { toast.error('Upload a file or describe your organisation'); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      if (uploadFile) fd.append('file', uploadFile);
      if (uploadDesc.trim()) fd.append('description', uploadDesc);
      const { data } = await api.post('/receptionist/ai-analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const extracted = data.extracted || {};
      setAnalysisResult(extracted);
      // Merge what the AI found into the profile; a field it found nothing for keeps its value.
      setProfile((p) => {
        const merged = { ...p };
        Object.entries(extracted).forEach(([k, v]) => {
          if (v && v.toString().trim() && k in p) merged[k] = v;
        });
        return merged;
      });
      toast.success('AI filled the fields it found. Review them, then save.');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'The analysis failed. Try again.');
    } finally { setAnalyzing(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const qr = (profile.quick_replies || '').split('\n').map((s) => s.trim()).filter(Boolean);
      await api.put('/receptionist/config', { ...profile, quick_replies: qr });
      toast.success('Company profile saved. The receptionist uses it straight away.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save the company profile');
    } finally { setSaving(false); }
  };

  const filledCount = analysisResult ? Object.values(analysisResult).filter((v) => v && v.toString().trim()).length : 0;

  // Plain render helpers (not components), so the inputs keep focus while typing.
  const pInput = (name, label, placeholder, opts = {}) => (
    <Field label={label} hint={opts.hint} required={opts.required}>
      {(id) => <input id={id} type="text" className="input-field" value={profile[name]} onChange={setP(name)} placeholder={placeholder} />}
    </Field>
  );
  const pText = (name, label, placeholder, opts = {}) => (
    <Field label={label} hint={opts.hint}>
      {(id) => <textarea id={id} rows={opts.rows || 3} className="input-field resize-y" value={profile[name]} onChange={setP(name)} placeholder={placeholder} />}
    </Field>
  );

  return (
    <Dialog open onClose={onClose} size="lg" title="AI receptionist settings"
      description="Voice and SMS, the company profile the receptionist speaks from, and the website widget."
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
          {tab === 'twilio' && (
            <button type="button" onClick={saveTwilio} disabled={saving || loading} className="btn-primary">
              {saving ? 'Saving…' : 'Save Twilio settings'}
            </button>
          )}
          {tab === 'profile' && (
            <button type="button" onClick={saveProfile} disabled={saving || loading} className="btn-primary">
              {saving ? 'Saving…' : 'Save company profile'}
            </button>
          )}
        </>
      )}>
      <Tabs value={tab} onChange={setTab} ariaLabel="Settings sections" tabs={[
        { id: 'twilio', label: 'Twilio voice and SMS' },
        { id: 'profile', label: 'Company profile' },
        { id: 'embed', label: 'Website widget' },
      ]} />

      <div className="pt-5">
        {loading ? <TableSkeleton rows={5} cols={2} /> : (
          <>
            {tab === 'twilio' && (
              <div className="space-y-4">
                <p className="text-[13px] leading-relaxed text-slate-600">
                  Get your credentials at <strong className="font-semibold text-slate-800">console.twilio.com</strong> → Account info.
                  They're saved to the server's <code className="rounded bg-slate-100 px-1 text-[12px]">.env</code> file, and no restart is needed.
                </p>
                <Field label="Account SID" required>
                  {(id) => (
                    <input id={id} type="text" className="input-field font-mono" value={twilio.account_sid} onChange={setT('account_sid')}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" autoComplete="off" />
                  )}
                </Field>
                <Field label="Auth token" required={!tokenSet}
                  hint={tokenSet ? `A token ending ${tokenLast4 || '••••'} is saved. Leave this blank to keep it, or paste a new one to replace it.` : 'Paste your Twilio auth token.'}
                  action={(
                    <button type="button" onClick={() => setShowToken((s) => !s)}
                      className="text-[12.5px] font-medium text-primary-700 underline-offset-2 hover:underline">
                      {showToken ? 'Hide' : 'Show'}
                    </button>
                  )}>
                  {(id) => (
                    <input id={id} type={showToken ? 'text' : 'password'} className="input-field font-mono" value={twilio.auth_token}
                      onChange={setT('auth_token')} placeholder={tokenSet ? `••••${tokenLast4}` : 'Your Twilio auth token'} autoComplete="off" />
                  )}
                </Field>
                <Field label="Twilio phone number" required hint="The number Twilio sends calls and SMS from, in +61 format (E.164).">
                  {(id) => <input id={id} type="text" className="input-field" value={twilio.phone_number} onChange={setT('phone_number')} placeholder="+61400000000" />}
                </Field>
                <Field label="Warm transfer number" hint="Your office or mobile. Urgent callers are put through to this number.">
                  {(id) => <input id={id} type="text" className="input-field" value={twilio.transfer_number} onChange={setT('transfer_number')} placeholder="+61400000000" />}
                </Field>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="mb-2 text-[13px] font-semibold text-slate-800">Paste these into your Twilio number's settings</p>
                  <dl className="space-y-2 text-[13px]">
                    {[['Voice (POST)', voiceUrl], ['SMS (POST)', smsUrl]].map(([label, url]) => (
                      <div key={label}>
                        <dt className="font-medium text-slate-700">{label}</dt>
                        <dd className="mt-0.5 flex items-start gap-1.5">
                          <code className="break-all rounded border border-slate-200 bg-white px-2 py-0.5 text-[12px] text-slate-800">{url}</code>
                          <CopyButton value={url} title={`Copy the ${label.split(' ')[0].toLowerCase()} webhook URL`} size={14} className="h-8 !px-2" />
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            )}

            {tab === 'profile' && (
              <div className="space-y-5">
                <section className="space-y-3 rounded-xl border border-slate-200 p-4">
                  <div>
                    <h3 className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
                      <Sparkles size={14} className="text-slate-400" aria-hidden="true" />Fill in your details from a document
                    </h3>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">
                      Upload your NDIS registration certificate (PDF or image) and AI extracts your company details into the
                      fields below. You can also type a description if you don't have the certificate handy.
                    </p>
                  </div>
                  <Field label="Certificate or document">
                    {(id) => (
                      <label htmlFor={id}
                        className={clsx('flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed px-4 py-3.5 transition-colors duration-150 focus-within:ring-2 focus-within:ring-primary-200',
                          uploadFile ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50')}>
                        <input id={id} type="file" className="sr-only" accept=".pdf,.png,.jpg,.jpeg,.webp"
                          onChange={(e) => setUploadFile((e.target.files && e.target.files[0]) || null)} />
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                          {uploadFile ? <Check size={16} className="text-emerald-600" aria-hidden="true" /> : <Upload size={16} aria-hidden="true" />}
                        </span>
                        <span className="min-w-0 text-[13px]">
                          {uploadFile ? (
                            <>
                              <span className="block truncate font-semibold text-emerald-800">{uploadFile.name}</span>
                              <span className="text-emerald-700">Ready to analyse</span>
                            </>
                          ) : (
                            <>
                              <span className="block font-medium text-slate-700">Choose a PDF or image</span>
                              <span className="text-slate-500">NDIS certificate, registration letter or website screenshot: anything with your details</span>
                            </>
                          )}
                        </span>
                      </label>
                    )}
                  </Field>
                  <Field label="Or describe your organisation (optional)">
                    {(id) => (
                      <textarea id={id} rows={3} className="input-field resize-y" value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
                        placeholder="For example: We are Skillwise Disability Services, ABN 12 345 678 901, an NDIS registered provider in Sydney. We provide Core Supports, SIL and Plan Management across Western Sydney and the Blue Mountains, and specialise in ASD, cerebral palsy and acquired brain injury." />
                    )}
                  </Field>
                  <button type="button" onClick={analyzeDoc} disabled={analyzing || (!uploadFile && !uploadDesc.trim())} className="btn-secondary">
                    {analyzing ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                    {analyzing ? 'Analysing with AI…' : 'Fill the fields with AI'}
                  </button>
                  {analysisResult && (
                    <p className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800" role="status">
                      <Check size={14} className="shrink-0" aria-hidden="true" />
                      AI filled {plural(filledCount, 'field')}. Review them below, then save.
                    </p>
                  )}
                </section>

                <ProfileSection title="Business details" hint="Who you are. The receptionist uses these when a caller asks how to reach or identify you.">
                  <FormRow cols={2}>
                    {pInput('company_name', 'Company or organisation name', 'Skillwise Disability Services', { required: true })}
                    {pInput('company_phone', 'Main contact phone', '+61 2 1234 5678')}
                    {pInput('company_email', 'Main contact email', 'info@company.com.au')}
                    {pInput('company_website', 'Website', 'https://company.com.au')}
                    {pInput('abn', 'ABN', '12 345 678 901')}
                    {pInput('ndis_provider_number', 'NDIS provider number', '4050012345')}
                    {pInput('registration_expiry', 'Registration expiry', '30 June 2026')}
                  </FormRow>
                  {pText('ndis_registration_groups', 'NDIS registration groups',
                    '0107 - Daily Activities\n0115 - Daily Personal Activities\n0125 - Participation in Community',
                    { hint: "The support categories your registration covers. The receptionist tells callers exactly what you're approved for." })}
                </ProfileSection>

                <ProfileSection title="Service info" hint="Where and how you operate. The receptionist uses these to match callers to your coverage.">
                  <FormRow cols={2}>
                    {pInput('service_areas', 'Service areas or suburbs', 'Greater Sydney, Parramatta, Blacktown, Western Sydney, Blue Mountains')}
                    {pInput('operating_hours', 'Operating hours', 'Monday–Friday 9am–5pm AEST')}
                    {pInput('languages_spoken', 'Languages spoken', 'English, Arabic, Mandarin')}
                  </FormRow>
                  {pInput('disability_types', 'Disability types or conditions you specialise in',
                    'Autism Spectrum Disorder (ASD), Cerebral Palsy, Down Syndrome, Acquired Brain Injury, Intellectual Disability, Mental Health',
                    { hint: "The receptionist uses this to connect callers' needs to your expertise." })}
                </ProfileSection>

                <ProfileSection title="Knowledge"
                  hint="The receptionist's knowledge base. Everything here is what it can talk about: services, pricing, processes, your team and anything else callers ask.">
                  {pText('services', 'Services you provide',
                    '- Core Supports: Daily personal care, community access, social participation, transport\n- SIL: 24/7 supported living for complex needs\n- Plan Management: NDIS invoice management, no out-of-pocket cost\n- Support Coordination: Help implementing NDIS plans\n- Community Nursing: Medication and wound care',
                    { rows: 7, hint: "One service per line, starting with '- '. The receptionist quotes these when callers ask what you offer." })}
                  {pText('pricing_info', 'Pricing information',
                    "We follow the current NDIS Price Guide.\nCore Support (weekday): from $65.09/hr\nEvenings, weekends and public holidays attract higher rates.\nPlan Management is funded from the participant's NDIS plan, with no out-of-pocket cost.\nSIL is individually quoted based on support intensity.",
                    { rows: 4, hint: 'The receptionist quotes this when callers ask about cost. Be as specific as you can.' })}
                  {pText('intake_process', 'Intake or onboarding process',
                    'After your enquiry, our intake coordinator will call within 1 business day to complete a needs assessment. We then match you with an appropriate support worker and send a service agreement.',
                    { hint: 'What happens after a caller enquires? The receptionist explains this step.' })}
                  {pText('referral_process', 'Referral process',
                    'Email referrals to referrals@company.com.au with participant name, NDIS number and support needs. We respond within 24 hours.',
                    { hint: 'How should support coordinators or other providers refer to you?' })}
                  {pText('staffing_info', 'Staffing information',
                    'All support workers hold minimum Certificate III in Individual Support, current NDIS Worker Screening Check and First Aid. We employ 45+ staff across Western Sydney.',
                    { rows: 2, hint: 'Team size, qualifications and screening. The receptionist uses this to build trust with callers.' })}
                  {pText('key_contacts', 'Key contacts or roles',
                    'Intake Coordinator: intake@company.com.au | Clinical Lead: nursing@company.com.au | CEO: John Smith',
                    { rows: 2, hint: 'Who should callers be directed to? The receptionist mentions these roles.' })}
                  {pText('welcome_message', 'Custom role or instructions (optional)',
                    'You are a warm and professional receptionist. Always mention our 24-hour intake response time. Prioritise callers looking for SIL placements.',
                    { rows: 2, hint: 'Replaces the default prompt. Leave blank to use the standard NDIS receptionist behaviour.' })}
                  {pText('additional_info', 'Anything else the receptionist should know',
                    'NDIS Quality & Safeguards Commission audited and approved.\nFully bilingual team (English + Arabic).\nQ: Do you have SIL vacancies? A: Yes, contact our SIL team at sil@company.com.au',
                    { hint: 'Awards, accreditations, what makes you different, common questions and answers: anything extra.' })}
                </ProfileSection>

                <ProfileSection title="Website widget">
                  <FormRow cols={2}>
                    <Field label="Chat bubble colour">
                      {(id) => (
                        <div className="flex items-center gap-3">
                          <input id={id} type="color" value={profile.primary_color} onChange={setP('primary_color')}
                            className="h-10 w-12 cursor-pointer rounded-lg border border-slate-200 bg-white p-1" />
                          <span className="font-mono text-[13px] text-slate-600">{profile.primary_color}</span>
                        </div>
                      )}
                    </Field>
                    <Field label="Position on your website">
                      {(id) => (
                        <select id={id} className="input-field" value={profile.widget_position} onChange={setP('widget_position')}>
                          <option value="right">Bottom right</option>
                          <option value="left">Bottom left</option>
                        </select>
                      )}
                    </Field>
                  </FormRow>
                  {pText('quick_replies', 'Quick reply buttons (one per line, up to 4)',
                    'I need NDIS support\nI have a referral\nWhat services do you offer?\nI need urgent help',
                    { rows: 4, hint: 'Shortcut buttons shown at the start of the chat on your website.' })}
                </ProfileSection>
              </div>
            )}

            {tab === 'embed' && (
              <div className="space-y-5">
                <p className="text-[13px] leading-relaxed text-slate-600">
                  Add this code to any website: WordPress, Wix, Squarespace or plain HTML. The chat widget connects to your
                  Rostery AI receptionist, and every lead comes into this page.
                </p>
                <div>
                  <p className="mb-2 text-[13px] font-medium text-slate-700">Embed code (paste before &lt;/body&gt;)</p>
                  <EmbedCode snippet={embedCode} />
                </div>
                <div>
                  <h3 className="mb-2 text-[13px] font-semibold text-slate-800">How to install</h3>
                  <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                    {INSTALL_STEPS.map(({ platform, steps }) => (
                      <li key={platform} className="px-4 py-3">
                        <p className="text-[13px] font-semibold text-slate-800">{platform}</p>
                        <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">{steps}</p>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="mb-2 text-[13px] font-semibold text-slate-800">What happens when someone chats on your website</h3>
                  <ul className="space-y-1.5">
                    {WIDGET_OUTCOMES.map((t) => (
                      <li key={t} className="flex items-start gap-2 text-[13px] leading-relaxed text-slate-700">
                        <Check size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
                  <span className="font-semibold">Production note:</span> the embed URL above uses{' '}
                  <code className="break-all">{backendBase}</code>. In production, replace it with your deployed backend URL
                  (for example <code>https://api.yourcompany.com</code>).
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

/* ─── Super admin: client SMS threads ───────────────────────────────────── */

const INTENT_TONE = { cancel: 'danger', complaint: 'danger', reschedule: 'warning', confirm: 'success', question: 'info', other: 'neutral' };

function SmsThreadsTab() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return api.get('/receptionist/sms-threads')
      .then((r) => setThreads(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e?.response?.data?.message || "We couldn't load the SMS conversations. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = threads.find((t) => t.id === selectedId) || null;
  const needAction = threads.filter((t) => t.needs_followup).length;
  const messages = Array.isArray(selected?.messages) ? selected.messages : [];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <SectionCard title="Client SMS conversations" icon={MessageSquare} bodyClass=""
        actions={needAction > 0 ? <StatusPill status="needs_followup" tone="danger" label={`${num(needAction)} need action`} /> : null}>
        {loading ? <TableSkeleton rows={5} cols={3} />
          : error ? <ErrorState message={error} onRetry={load} />
            : threads.length === 0 ? (
              <EmptyState icon={MessageSquare} title="No SMS conversations yet"
                hint="Conversations appear here when existing clients reply to reminder texts." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {threads.map((t) => {
                  const on = t.id === selectedId;
                  return (
                    <li key={t.id}>
                      <button type="button" onClick={() => setSelectedId(t.id)} aria-pressed={on}
                        className={clsx('w-full px-5 py-3.5 text-left transition-colors duration-150', on ? 'bg-primary-50/70' : 'hover:bg-slate-50')}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-semibold text-slate-800">{t.client_name || t.phone}</span>
                              {t.needs_followup && (
                                <>
                                  <span className="h-2 w-2 shrink-0 rounded-full bg-rose-500" aria-hidden="true" />
                                  <span className="sr-only">Needs follow-up</span>
                                </>
                              )}
                            </div>
                            <p className="mt-0.5 font-mono text-[12px] text-slate-500">{t.phone}</p>
                            {t.last_message && <p className="mt-1 line-clamp-1 text-[13px] text-slate-600">{t.last_message}</p>}
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-1">
                            {t.intent_flag && <StatusPill status={t.intent_flag} tone={INTENT_TONE[t.intent_flag] || 'neutral'} label={humanise(t.intent_flag)} />}
                            <span className="text-[12px] tabular-nums text-slate-500">{fmtDate(t.updated_at)}</span>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
      </SectionCard>

      <SectionCard title={selected ? (selected.client_name || selected.phone) : 'Conversation'} bodyClass=""
        actions={selected?.needs_followup ? <StatusPill status="needs_followup" tone="danger" label="Needs staff follow-up" /> : null}>
        {!selected ? (
          <EmptyState icon={MessageSquare} title="Choose a conversation" hint="Select a thread to read its messages." />
        ) : (
          <>
            {selected.client_name && <p className="border-b border-slate-100 px-5 py-2 font-mono text-[12px] text-slate-500">{selected.phone}</p>}
            <div className="max-h-[400px] space-y-2.5 overflow-y-auto bg-slate-50 p-4">
              {messages.length === 0 ? <p className="text-[13px] text-slate-500">No messages in this thread.</p> : messages.map((m, i) => {
                const client = m.role === 'user';
                return (
                  <div key={i} className={clsx('flex flex-col', client ? 'items-end' : 'items-start')}>
                    <span className="mb-0.5 text-[11.5px] font-medium text-slate-500">{client ? 'Client' : 'Receptionist'}</span>
                    <span className={clsx('max-w-[82%] whitespace-pre-wrap rounded-xl px-3 py-2 text-[13px] leading-relaxed',
                      client ? 'bg-slate-800 text-white' : 'border border-slate-200 bg-white text-slate-700')}>
                      {m.content}
                    </span>
                  </div>
                );
              })}
            </div>
            {selected.intent_detail && (
              <div className="border-t border-slate-100 px-5 py-3">
                <p className="text-[12.5px] font-semibold text-slate-700">AI detected intent: {humanise(selected.intent_flag) || '—'}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">{selected.intent_detail}</p>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}

/* ─── Main dashboard ─────────────────────────────────────────────────────── */
export default function LeadsDashboard() {
  // Role-aware: super_admin sees Rostery's global console (all tabs, Twilio .env settings);
  // org admin / office users see the same dashboard scoped to their own org (no Twilio .env,
  // SMS threads or outbound calls).
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin';
  const modules = useModules();
  const canWebChat = isSuper || modules.ai_chatbot === true;
  const confirm = useConfirm();
  const { id: routeLeadId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);          // the list, with the status/channel/score filters applied by the server
  const [allLeads, setAllLeads] = useState([]);    // the latest leads, unfiltered: the Today queue and the counts
  const [orgConfig, setOrgConfig] = useState(null); // org's masked /my-config (drives the status pill)
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedTab, setSelectedTab] = useState('details');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterScore, setFilterScore] = useState('');
  const [showTwilio, setShowTwilio] = useState(false);
  const [chatSetupOpen, setChatSetupOpen] = useState(false);
  const [assignees, setAssignees] = useState([]);  // [{ id, name, role }] for assign-to-user
  const [exporting, setExporting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [helpOpen, setHelpOpen] = useState(null);  // null = follow the default (open only while not set up)
  const [showAllQueue, setShowAllQueue] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // The channel filter lives in the address (?channel=web_chat), so /chatbot can land on web chat.
  const rawChannel = searchParams.get('channel') || '';
  const filterChannel = CHANNEL_KEYS.includes(rawChannel) ? rawChannel : '';
  const setFilterChannel = useCallback((value) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set('channel', value); else p.delete('channel');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const tabIds = isSuper ? ['today', 'all', 'phone', 'callbacks', 'outbound', 'sms'] : ['today', 'all', 'phone', 'callbacks'];
  const [tab, setTab] = useUrlTab('tab', 'today', tabIds);

  const load = useCallback(async (silent = false) => {
    if (!silent) { setLoading(true); setLoadError(null); }
    try {
      const params = new URLSearchParams();
      if (filterStatus)  params.set('status', filterStatus);
      if (filterChannel) params.set('channel', filterChannel);
      if (filterScore)   params.set('min_score', filterScore);
      params.set('limit', String(LEAD_LIMIT));
      const filtered = !!(filterStatus || filterChannel || filterScore);
      // READS switch by role. Org config (status pill) is fetched only for orgs; super admins
      // never hit /my-* and orgs never hit the super-only endpoints.
      const leadsPath = isSuper ? '/receptionist/leads' : '/receptionist/my-leads';
      const statsPath = isSuper ? '/receptionist/stats' : '/receptionist/my-stats';
      const [listRes, statsRes, allRes, cfgRes] = await Promise.all([
        api.get(`${leadsPath}?${params}`),
        api.get(statsPath),
        filtered ? api.get(`${leadsPath}?limit=${LEAD_LIMIT}`) : Promise.resolve(null),
        isSuper ? Promise.resolve(null) : api.get('/receptionist/my-config').catch(() => null),
      ]);
      const list = Array.isArray(listRes.data?.leads) ? listRes.data.leads : [];
      const all = filtered ? (Array.isArray(allRes?.data?.leads) ? allRes.data.leads : []) : list;
      setLeads(list);
      setAllLeads(all);
      setStats(statsRes.data || null);
      if (!isSuper) setOrgConfig(cfgRes ? (cfgRes.data || {}) : null);
      // Keep an open lead in step with what was just loaded (a callback scheduled, a status changed).
      setSelected((s) => (s ? all.find((l) => l.id === s.id) || list.find((l) => l.id === s.id) || s : s));
      setNow(Date.now());
      setLoadError(null);
    } catch (e) {
      if (silent) toast.error('Could not refresh the leads');
      else setLoadError(e?.response?.data?.message || "We couldn't load the leads. Check your connection, then try again.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filterStatus, filterChannel, filterScore, isSuper]);

  useEffect(() => { load(); }, [load]);

  // Overdue and due-today move with the clock, not only with a reload.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Load the assignable users once (drives the "Assigned to" select on each lead).
  useEffect(() => {
    api.get('/receptionist/assignees')
      .then((r) => setAssignees(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAssignees([]));
  }, []);

  // /receptionist/leads/:id (super admin) opens that lead over the dashboard.
  const routeOpened = useRef(null);
  useEffect(() => {
    if (!routeLeadId || loading || routeOpened.current === routeLeadId) return;
    routeOpened.current = routeLeadId;
    const match = (l) => String(l.id) === String(routeLeadId);
    const found = allLeads.find(match) || leads.find(match);
    if (found) { setSelectedTab('details'); setSelected(found); return; }
    if (isSuper) {
      api.get(`/receptionist/leads/${routeLeadId}`)
        .then((r) => { if (r.data && r.data.id) { setSelectedTab('details'); setSelected(r.data); } })
        .catch(() => toast.error('That lead could not be found'));
    }
  }, [routeLeadId, loading, allLeads, leads, isSuper]);

  const patchLead = useCallback((id, patch) => {
    const apply = (ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l));
    setLeads(apply);
    setAllLeads(apply);
    setSelected((s) => (s && s.id === id ? { ...s, ...patch } : s));
  }, []);

  const openLead = useCallback((lead, initialTab = 'details') => {
    setSelectedTab(initialTab);
    setSelected(lead);
  }, []);
  const closeLead = useCallback(() => setSelected(null), []);
  const closeTwilio = useCallback(() => setShowTwilio(false), []);
  const closeChatSetup = useCallback(() => setChatSetupOpen(false), []);
  const refreshSilently = useCallback(() => load(true), [load]);

  const markContacted = async (lead) => {
    setBusyId(lead.id);
    try {
      await api.put(`/receptionist/leads/${lead.id}`, { status: 'contacted' });
      patchLead(lead.id, { status: 'contacted' });
      toast.success(`${leadName(lead)} marked as contacted`);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not update the lead');
    } finally { setBusyId(null); }
  };

  const markCallbackDone = async (lead) => {
    setBusyId(lead.id);
    try {
      await api.put(`/receptionist/leads/${lead.id}`, { callback_done: true });
      patchLead(lead.id, { callback_done: true });
      toast.success('Callback marked done');
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not update the callback');
    } finally { setBusyId(null); }
  };

  // Permanently delete a lead (asks first), then drop it from the page.
  const deleteLead = async (lead) => {
    const ok = await confirm(
      `${leadName(lead)} is removed for good, with their notes and transcript. This cannot be undone.`,
      { title: 'Delete this lead?', confirmLabel: 'Delete lead', danger: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/receptionist/leads/${lead.id}`);
      toast.success('Lead deleted');
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      setAllLeads((ls) => ls.filter((l) => l.id !== lead.id));
      setSelected((s) => (s?.id === lead.id ? null : s));
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not delete the lead');
    }
  };

  // Assign a lead to a user (or unassign with ''), then patch local state.
  const assignLead = async (id, value) => {
    try {
      await api.put(`/receptionist/leads/${id}`, { assigned_to: value || null });
      patchLead(id, { assigned_to: value || null });
      toast.success(value ? 'Lead assigned' : 'Lead unassigned');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not update the assignment');
    }
  };

  // Export the currently filtered leads to CSV.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filterStatus)  params.status    = filterStatus;
      if (filterChannel) params.channel   = filterChannel;
      if (filterScore)   params.min_score = filterScore;
      const res = await api.get('/receptionist/leads/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'receptionist-leads.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not export the CSV');
    } finally { setExporting(false); }
  };

  const clearFilters = () => { setSearch(''); setFilterStatus(''); setFilterScore(''); setFilterChannel(''); };
  const showNewLeads = () => { setFilterStatus('new'); setTab('all'); };

  /* ── Derived ── */
  const queue = useMemo(() => buildQueue(allLeads, now), [allLeads, now]);
  const today = useMemo(() => countToday(allLeads, now), [allLeads, now]);
  const pendingCallbacks = useMemo(() => allLeads.filter(hasPendingCallback)
    .map((lead) => ({ lead, ...callbackFlags(lead, now), crisis: false, hot: false, missed: false }))
    .sort((a, b) => (a.callbackAt || 0) - (b.callbackAt || 0)), [allLeads, now]);
  const callbackStats = useMemo(() => {
    const sod = startOfDay(now);
    const eod = endOfDay(now);
    return {
      pending: pendingCallbacks.length,
      overdue: pendingCallbacks.filter((i) => i.overdue).length,
      today: pendingCallbacks.filter((i) => i.callbackAt != null && i.callbackAt >= sod && i.callbackAt <= eod).length,
      done: allLeads.filter((l) => l.callback_done).length,
    };
  }, [pendingCallbacks, allLeads, now]);
  const appointments = useMemo(() => allLeads
    .filter((l) => (toTime(l.appointment_at) || 0) > now)
    .sort((a, b) => toTime(a.appointment_at) - toTime(b.appointment_at)), [allLeads, now]);
  const phoneStats = useMemo(() => {
    const phoneLeads = allLeads.filter((l) => PHONE_CHANNELS.includes(l.channel));
    const durations = phoneLeads.map((l) => Number(l.call_duration) || 0).filter((d) => d > 0);
    return {
      count: phoneLeads.length,
      voicemails: allLeads.filter((l) => l.channel === 'phone_voicemail').length,
      withRecordings: phoneLeads.filter((l) => l.recording_url).length,
      avg: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0,
    };
  }, [allLeads]);
  const warmCount = useMemo(() => allLeads.filter((l) => { const s = scoreOf(l); return s >= 31 && s < 61; }).length, [allLeads]);
  const phoneList = useMemo(() => leads.filter((l) => PHONE_CHANNELS.includes(l.channel)), [leads]);
  const callbackList = useMemo(() => leads.filter((l) => l.callback_at), [leads]);

  // Set up? Super admins: Twilio in the server .env. Organisations: their own receptionist switch.
  // null = not known yet, which keeps the help collapsed and the pill hidden.
  const configured = isSuper ? (stats ? !!stats.twilio_configured : null) : (orgConfig ? !!orgConfig.enabled : null);
  const helpExpanded = helpOpen == null ? configured === false : helpOpen;
  const noData = loading && !allLeads.length;
  const kpi = (n) => (noData || loadError ? '—' : num(n));
  const statNum = (n) => (loadError || n == null ? '—' : num(n));

  const statusPill = configured == null ? null : (
    <StatusPill status={configured ? 'active' : 'inactive'}
      label={configured ? (isSuper ? 'Twilio active' : 'Receptionist active') : (isSuper ? 'Twilio not configured' : 'Not configured')} />
  );

  const setupMessage = isSuper
    ? 'Voice calls and SMS are off until you add your Twilio credentials. Use the Settings button above to add them.'
    : isAdmin
      ? 'Add your phone keys and knowledge with the Settings button above to start capturing leads from calls, SMS and web chat.'
      : 'An admin can finish setting it up in Settings → AI receptionist. Until then the receptionist can’t answer calls or texts.';
  const setupNote = configured === false ? (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="text-[13px] leading-relaxed text-amber-900">
        <p className="font-semibold">{isSuper ? 'Twilio isn’t configured' : 'The receptionist isn’t set up yet'}</p>
        <p className="mt-0.5">{setupMessage}</p>
      </div>
    </div>
  ) : null;
  const help = (
    <HowItWorks open={helpExpanded} onToggle={() => setHelpOpen(!helpExpanded)} setupNote={setupNote} />
  );

  const webChatNotice = filterChannel === 'web_chat' && canWebChat ? (
    <div className="flex flex-col gap-2 rounded-lg bg-slate-50 px-3 py-2 sm:flex-row sm:items-center">
      <p className="flex flex-1 items-center gap-2 text-[13px] text-slate-600">
        <MessageCircle size={15} className="shrink-0 text-slate-400" aria-hidden="true" />
        Web-chat leads come from the chat widget on your website.
      </p>
      <button type="button" className="btn-secondary btn-sm h-9 self-start sm:self-auto" onClick={() => setChatSetupOpen(true)}>
        Website chat settings
      </button>
    </div>
  ) : null;

  const listProps = {
    loading, error: loadError, onRetry: () => load(), now,
    search, onSearch: setSearch, channel: filterChannel, onChannel: setFilterChannel,
    status: filterStatus, onStatus: setFilterStatus, score: filterScore, onScore: setFilterScore,
    channelCounts: stats?.byChannel, total: stats?.total, assignees,
    onOpen: openLead, onDelete: deleteLead, onAssign: assignLead, onMarkContacted: markContacted,
    onClearFilters: clearFilters, limitReached: leads.length >= LEAD_LIMIT, notice: webChatNotice,
  };

  const tabs = [
    { id: 'today', label: 'Today', count: queue.length || undefined },
    { id: 'all', label: 'All leads', count: stats?.total || undefined },
    { id: 'phone', label: 'Phone and voicemail' },
    { id: 'callbacks', label: 'Callbacks', count: pendingCallbacks.length || undefined },
    ...(isSuper ? [{ id: 'outbound', label: 'Outbound calls' }, { id: 'sms', label: 'SMS threads' }] : []),
  ];

  return (
    <div className={PAGE_WIDE}>
      {/* Actions (the top bar already names the page) */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-h-[24px] flex-wrap items-center gap-2">{statusPill}</div>
        <div className="flex flex-wrap items-center gap-2">
          {isSuper ? (
            <button type="button" onClick={() => setShowTwilio(true)} className="btn-secondary btn-sm h-9">
              <Settings size={14} aria-hidden="true" /> Settings
            </button>
          ) : isAdmin ? (
            <Link to="/settings/ai-receptionist" className="btn-secondary btn-sm h-9">
              <Settings size={14} aria-hidden="true" /> Settings
            </Link>
          ) : null}
          <button type="button" onClick={exportCsv} disabled={exporting} className="btn-secondary btn-sm h-9">
            <Download size={14} aria-hidden="true" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button type="button" onClick={() => load()} disabled={loading} className="btn-secondary btn-sm h-9">
            <RefreshCw size={14} className={clsx(loading && 'animate-spin')} aria-hidden="true" /> Refresh
          </button>
        </div>
      </div>

      <Tabs tabs={tabs} value={tab} onChange={setTab} ariaLabel="Receptionist sections" />

      {/* TODAY */}
      {tab === 'today' && (
        <div className="space-y-6">
          {configured === false && help}
          <StatRow cols={4} divided={false}>
            <KpiTile label="New today" value={kpi(stats?.today ?? today.created)} icon={Inbox} accent="slate"
              sub={stats && !loadError ? `${num(stats.this_week || 0)} in the last 7 days` : undefined} />
            <KpiTile label="Hot leads" value={kpi(today.hot)} icon={Flame} accent="slate" alert={!loadError && today.hot > 0}
              sub="Score 61+, not yet contacted" />
            <KpiTile label="Callbacks due" value={kpi(today.due)} icon={Bell} accent="slate" alert={!loadError && today.due > 0}
              sub={today.overdue ? `${num(today.overdue)} overdue` : 'Due today or overdue'} />
            <KpiTile label="Missed calls" value={kpi(today.missed)} icon={PhoneMissed} accent="slate" alert={!loadError && today.missed > 0}
              sub="Voicemails and unanswered calls" />
          </StatRow>

          {!loadError && stats?.missed_leads > 0 && (
            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-start gap-2.5">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
                <p className="text-[13px] leading-relaxed text-slate-700">
                  <span className="font-semibold text-slate-900">{plural(stats.missed_leads, 'lead')} not contacted after 24 hours.</span>{' '}
                  They came in more than a day ago and are still marked New.
                </p>
              </div>
              <button type="button" onClick={showNewLeads} className="btn-secondary btn-sm h-9 self-start sm:self-auto">Show new leads</button>
            </div>
          )}

          <SectionCard title="Needs action" bodyClass=""
            actions={!noData && !loadError ? <span className="text-[12.5px] tabular-nums text-slate-500">{plural(queue.length, 'lead')}</span> : null}>
            {noData ? <TableSkeleton rows={4} cols={4} />
              : loadError ? <ErrorState message={loadError} onRetry={() => load()} />
                : queue.length === 0 ? (
                  <EmptyState icon={CheckCircle} title="Nothing needs action right now"
                    hint="Hot leads, callbacks due today and missed calls appear here as they come in. Every enquiry is in All leads."
                    action={<button type="button" className="btn-secondary btn-sm h-9" onClick={() => setTab('all')}>View all leads</button>} />
                ) : (
                  <>
                    <ul className="divide-y divide-slate-100">
                      {(showAllQueue ? queue : queue.slice(0, QUEUE_PREVIEW)).map((item) => (
                        <QueueRow key={item.lead.id} item={item} busy={busyId === item.lead.id} onOpen={openLead}
                          onMarkContacted={markContacted} onCallbackDone={markCallbackDone} />
                      ))}
                    </ul>
                    {queue.length > QUEUE_PREVIEW && (
                      <div className="border-t border-slate-100 px-4 py-2">
                        <button type="button" onClick={() => setShowAllQueue((v) => !v)} className="btn-ghost btn-sm h-9">
                          {showAllQueue ? 'Show fewer' : `Show all ${num(queue.length)}`}
                        </button>
                      </div>
                    )}
                    {allLeads.length >= LEAD_LIMIT && (
                      <p className="border-t border-slate-100 px-5 py-2.5 text-[12.5px] text-slate-500">
                        Worked out from the latest {num(LEAD_LIMIT)} leads.
                      </p>
                    )}
                  </>
                )}
          </SectionCard>

          {!loadError && <AppointmentsCard appointments={appointments} total={stats?.upcoming_appointments} onOpen={openLead} />}

          {configured !== false && help}
        </div>
      )}

      {/* ALL LEADS */}
      {tab === 'all' && (
        <div className="space-y-6">
          <StatRow cols={4} divided={false}>
            <KpiTile label="Total leads" value={statNum(stats?.total)} icon={Users} accent="slate"
              sub={stats && !loadError ? `${num(stats.this_week || 0)} in the last 7 days` : undefined} />
            <KpiTile label="Hot (61+)" value={statNum(stats?.hot)} icon={Flame} accent="slate" sub="Instant alert and warm transfer" />
            <KpiTile label="Warm (31–60)" value={kpi(warmCount)} icon={Zap} accent="slate"
              sub={allLeads.length >= LEAD_LIMIT ? `Of the latest ${num(LEAD_LIMIT)} leads` : 'Email alert to the team'} />
            <KpiTile label="Converted" value={stats && !loadError ? `${stats.conversion_rate || 0}%` : '—'} icon={CheckCircle} accent="slate"
              sub={stats && !loadError ? plural(stats.converted || 0, 'lead') : undefined} />
          </StatRow>
          <LeadList {...listProps} leads={leads}
            empty={{ icon: Users, title: 'No leads yet', hint: 'Leads appear here as soon as the receptionist captures an enquiry by phone, SMS or web chat.' }} />
          {stats && !loadError && <Breakdown stats={stats} />}
        </div>
      )}

      {/* PHONE AND VOICEMAIL */}
      {tab === 'phone' && (
        <div className="space-y-6">
          <StatRow cols={4} divided={false}>
            <KpiTile label="Phone and SMS leads" value={kpi(phoneStats.count)} icon={PhoneCall} accent="slate" />
            <KpiTile label="Voicemails" value={kpi(phoneStats.voicemails)} icon={Voicemail} accent="slate" />
            <KpiTile label="With recordings" value={kpi(phoneStats.withRecordings)} icon={Mic} accent="slate" />
            <KpiTile label="Average call length" value={noData || loadError || !phoneStats.avg ? '—' : fmtClock(phoneStats.avg)}
              icon={Timer} accent="slate" sub="Minutes and seconds" />
          </StatRow>
          {configured != null && (
            <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
              <div className="shrink-0">{statusPill}</div>
              <p className="text-[13px] text-slate-600">
                {configured ? 'Inbound calls, voicemail and SMS are live.'
                  : isSuper ? 'Voice calls and SMS are off until you add your Twilio credentials in Settings.'
                    : isAdmin ? 'Add your phone keys and knowledge in Settings to turn on calls and SMS.'
                      : 'An admin can add the phone keys in Settings → AI receptionist to turn on calls and SMS.'}
              </p>
            </div>
          )}
          <LeadList {...listProps} leads={phoneList}
            empty={{ icon: PhoneCall, title: 'No phone leads yet', hint: 'Phone leads appear here once Twilio is set up and callers ring your number.' }} />
        </div>
      )}

      {/* CALLBACKS */}
      {tab === 'callbacks' && (
        <div className="space-y-6">
          <StatRow cols={4} divided={false}>
            <KpiTile label="Pending" value={kpi(callbackStats.pending)} icon={Bell} accent="slate" />
            <KpiTile label="Overdue" value={kpi(callbackStats.overdue)} icon={AlertTriangle} accent="slate"
              alert={!loadError && callbackStats.overdue > 0} />
            <KpiTile label="Due today" value={kpi(callbackStats.today)} icon={CalendarClock} accent="slate" />
            <KpiTile label="Completed" value={kpi(callbackStats.done)} icon={CheckCircle} accent="slate" />
          </StatRow>
          <SectionCard title="Pending callbacks" bodyClass=""
            actions={!noData && !loadError ? <span className="text-[12.5px] tabular-nums text-slate-500">{plural(pendingCallbacks.length, 'callback')}</span> : null}>
            {noData ? <TableSkeleton rows={3} cols={4} />
              : loadError ? <ErrorState message={loadError} onRetry={() => load()} />
                : pendingCallbacks.length === 0 ? (
                  <EmptyState icon={CheckCircle} title="No pending callbacks"
                    hint="Callbacks appear here when a caller asks to be called back, or when you schedule one from a lead." />
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {pendingCallbacks.map((item) => (
                      <QueueRow key={item.lead.id} item={item} busy={busyId === item.lead.id} onOpen={openLead}
                        onMarkContacted={markContacted} onCallbackDone={markCallbackDone} />
                    ))}
                  </ul>
                )}
          </SectionCard>
          <LeadList {...listProps} title="All callbacks" leads={callbackList} showCallback
            empty={{ icon: Bell, title: 'No callbacks scheduled', hint: 'Callbacks are created when a caller asks for one, or when you schedule one from a lead.' }} />
        </div>
      )}

      {/* SUPER ADMIN ONLY: outbound calls and client SMS threads (no org endpoints) */}
      {isSuper && tab === 'outbound' && <OutboundCalls embedded />}
      {isSuper && tab === 'sms' && <SmsThreadsTab />}

      {selected && (
        <LeadModal key={selected.id} lead={selected} initialTab={selectedTab} onClose={closeLead} onUpdated={refreshSilently}
          assignees={assignees} onAssign={assignLead} onDelete={deleteLead} onCallbackDone={markCallbackDone} />
      )}
      {/* Twilio (.env) + company profile + widget embed — super admin only */}
      {isSuper && showTwilio && <TwilioSettingsModal onClose={closeTwilio} onSaved={refreshSilently} />}
      {canWebChat && (
        <Dialog open={chatSetupOpen} onClose={closeChatSetup} size="md" title="Website chat"
          description="The chat widget on your website. Its enquiries appear here as web-chat leads."
          footer={<button type="button" onClick={closeChatSetup} className="btn-secondary">Close</button>}>
          <WebsiteChatSection bare />
        </Dialog>
      )}
    </div>
  );
}
